// ============================================================
// THE LOOKS' KIT (plan body-map H3): what every special's look is given -
// the struck place, its part, when, a seed - and the helpers the looks
// share (a glow on the struck part, a copy of the whole drawing, a point
// round the place, the swing across a part, motes drawn in, thrown, or
// flung up as sparks). The two that draw the art itself (`partGlow`,
// `wholeLines`) draw nothing on the goo, which has no lines.
// ============================================================

import type { CSSProperties } from 'react';
import type { DamageCode } from '../../../../engine/impacts';
import type { Box, Point } from '../../../../engine/plate/frame';
import { Glow, type Mote } from './primitives';

/** What a look is given: the struck place, its part, when, and how to draw at this plate's scale. */
export interface LookCtx {
  u: string;
  /** Unique to this moment (its pieces' defs). */
  id: string;
  at: Point;
  /** The struck part (its clip, `<u>-c-<part>`) and its box. */
  part: string;
  partBox: Box;
  /** The whole drawing's box. */
  figureBox: Box;
  start: number;
  /** 1 on a figure's frame; smaller on the goo's. */
  k: number;
  type: DamageCode;
  /** The plate has line art and per-part clips (a person, a vehicle); the goo has neither. */
  lines: boolean;
  /** The body has Daemonic protection (Exorcist runs over all of it). */
  daemonic: boolean;
  /** Every place's anchor (Numbing frosts both arms). */
  anchors: Partial<Record<string, Point>>;
  /** How many sizes above Average the body is (Titanbane shakes the ground by it; nothing at 0). */
  sizeSteps: number;
  /** Seeded numbers in [0, 1), the same on every screen. */
  r: (salt: string | number) => number;
  /** The plate's soft glow filter for the specials. */
  glow: string;
}

export const around = (c: LookCtx, spread: number, salt: string): Point => [
  c.at[0] + (c.r(`${salt}x`) - 0.5) * spread * c.k,
  c.at[1] + (c.r(`${salt}y`) - 0.5) * spread * c.k,
];

interface PartGlow {
  /** How far round the place (frame units at a figure's scale). */
  r: number;
  palette: string;
  dur: number;
  /** When, if not with the hit. */
  start?: number;
  /** Line width, if not the glow's own. */
  width?: number;
  /** Its filter, if not the specials' soft glow (a disruption, a mist, `none`). */
  filter?: string;
  /** Its class, if not the glow's (a burn). */
  className?: string;
  /** Another part, and the place on it, if not the struck one (Numbing's arms and head). */
  part?: string;
  at?: Point;
}

/** A glow on a part (the struck one by default) round a place, when the plate has lines to glow. */
export function partGlow(c: LookCtx, { r, start = c.start, part = c.part, at = c.at, ...look }: PartGlow) {
  return (
    c.lines && (
      <Glow
        key={`${part}${start}${r}`}
        u={c.u}
        id={`${c.id}${part}g${Math.round(start * 100)}r${Math.round(r)}`}
        part={part}
        at={at}
        r={r * c.k}
        start={start}
        {...look}
      />
    )
  );
}

/** A copy of the whole drawing's lines, for a look over all of it (the goo has none). */
export const wholeLines = (c: LookCtx, className: string, style: CSSProperties, extra: Record<string, unknown> = {}) =>
  c.lines && (
    <g className={className} style={style} {...extra}>
      <use href={`#${c.u}-art`} fill="currentColor" />
    </g>
  );

/** The swing across a part: diagonal over a trunk, across a limb; long enough to cross it. */
export function swingOf(c: LookCtx): { ang: number; len: number } {
  const { ang, len } = swingAcross(c.partBox, c.r);
  return { ang, len: len * c.k };
}

/** The swing across a part's box (frame units, a figure's scale), its side the seed's: the look and the move agree. */
export function swingAcross([x0, y0, x1, y1]: Box, r: (salt: string) => number): { ang: number; len: number } {
  const [w, h] = [x1 - x0, y1 - y0];
  const limb = h > w * 1.6;
  const ang = limb ? (r('side') < 0.5 ? -0.45 : 0.45) : -0.65;
  return { ang, len: Math.min(900, Math.max(380, limb ? w * 2.2 : Math.hypot(w, h) * 0.85)) };
}

/**
 * Bits thrown along a direction (radians) and falling as they fly (`lift`: how far they drop - up, if negative),
 * `spread` either side, out to about `dist`, from the place (or `from`).
 */
export function thrown(
  c: LookCtx,
  o: { n: number; dir: number; spread: number; dist: number; size: number; salt: string; from?: Point; lift?: number },
): Mote[] {
  const { from = c.at, lift = 120 } = o;
  return Array.from({ length: o.n }, (_, i) => {
    const a = o.dir + (c.r(`${o.salt}a${i}`) - 0.5) * o.spread;
    const d = o.dist * (0.5 + 0.8 * c.r(`${o.salt}d${i}`)) * c.k;
    return { from, by: [d * Math.cos(a), d * Math.sin(a) + lift * c.k], delay: 0, r: o.size * c.k };
  });
}

/** A blacksmith's spray: hot sparks flung up and out from the place, arcing down under their own weight. */
export function forgeSparks(c: LookCtx, n: number, salt: string): Mote[] {
  return Array.from({ length: n }, (_, i) => {
    const a = -Math.PI / 2 + (c.r(`${salt}a${i}`) - 0.5) * 2.6;
    const v = (220 + 520 * c.r(`${salt}v${i}`)) * c.k;
    return { from: c.at, by: [Math.cos(a) * v, Math.sin(a) * v + 380 * c.k], delay: 0.02 * (i % 6), r: 5 * c.k };
  });
}

/** Motes drawn in from all round to the place: how many, from how far (and how much farther at most), staggered. */
export function drawnIn(
  c: LookCtx,
  o: { n: number; from: number; spread: number; salt: string; stagger: number },
): Mote[] {
  return Array.from({ length: o.n }, (_, i) => {
    const a = (i / o.n) * Math.PI * 2 + c.r(`${o.salt}a${i}`) * 0.6;
    const r = (o.from + o.spread * c.r(`${o.salt}r${i}`)) * c.k;
    const from: Point = [c.at[0] + r * Math.cos(a), c.at[1] + r * Math.sin(a)];
    return { from, by: [-r * Math.cos(a), -r * Math.sin(a)], delay: i * o.stagger, r: 8 * c.k };
  });
}
